import { InjectionKey } from 'vue'
import { ActionTree, createStore, GetterTree, MutationTree, Store, StoreOptions, useStore } from 'vuex'
import { EDeviceTypeName } from '../types'
import { Device, DeviceHms, DeviceOsd, DeviceStatus, DockOsd, GatewayOsd, OSDVisible } from '../types/device'
import { getLayers } from '/@/api/layer'
import { LayerType } from '/@/types/mapLayer'
import { WaylineFile } from '/@/types/wayline'
import { DevicesCmdExecuteInfo } from '/@/types/device-cmd'
import { setAlgorithmTaskStatus, getAlgorithmTaskListPageVo } from '/@/api/alarm'
import { getWaylineJobs, getFlyAlgorithmTaskList } from '/@/api/wayline'
import { ELocalStorageKey, ERouterName } from '/@/types/enums'
import { RouteRecordRaw } from 'vue-router'
import { constantRoutes } from '/@/router/index'
import robotModule, { getRobotInitialState, type RobotState } from './modules/robot'
import {
  applyRobotDogPageMeta,
  ROBOT_DOG_MEDIA_CACHE_NAME,
  ROBOT_DOG_MEDIA_COMPONENT,
  ROBOT_DOG_MEDIA_FILES_CACHE_NAME,
} from '/@/utils/robot-dog-page-cache'
import { applyWorkspacePageMeta } from '/@/utils/workspace-page-cache'
import AlarmCenter from '/@/components/algorithm/center.vue'
import Model from '/@/components/algorithm/model.vue'
import Binding from '/@/components/algorithm/binding.vue'
import PushLog from '/@/components/algorithm/pushLog.vue'
const WaylinePanel = () => import('/@/pages/page-web/projects/wayline.vue')
const CreatePlan = () => import('/@/components/task/CreatePlan.vue')
const algorithmPlan = () => import('/@/components/algorithm/index.vue')
const DockPanel = () => import('/@/pages/page-web/projects/dock.vue')
const modules = import.meta.glob('../pages/page-web/**/*.vue')

const initStateFunc = () => ({
  sidebarRouters: [] as RouteRecordRaw[],
  /** 是否已根据本地菜单执行过动态 addRoute（勿用 sidebar 是否为空判断，菜单为空时也会一直是 []） */
  dynamicRoutesRegistered: false,
  // 智能助手侧边栏显示状态
  showAssistantSidebar: false,
  speakerAudio: {
    md5: '',
    progress: { percent: 100, step_key: 'play' },
    psdk_index: 2,
    status: 'success'
  },
  speakerVolume: {
    volume: 100,
  },
  isPowered: false,
  Layers: [
    {
      name: 'default',
      id: '',
      is_distributed: true,
      elements: [],
      is_check: false,
      is_select: false,
      type: 1
    },
    {
      name: 'share',
      id: '',
      is_distributed: true,
      elements: [],
      is_check: false,
      is_select: false,
      type: 2
    }
  ],
  layerBaseInfo: {} as {
    [key:string]:string
  },
  drawVisible: false,
  livestreamOthersVisible: false,
  livestreamAgoraVisible: false,
  coverList: [

  ] as any,
  wsEvent: {
    mapElementCreat: {},
    mapElementUpdate: {},
    mapElementDelete: {}
  },
  deviceStatusEvent: {
    deviceOnline: {} as DeviceStatus,
    deviceOffline: {}
  },
  markerInfo: {
    coverMap: {} as {
      [sn: string]: any
    },
    pathMap: {} as {
      [sn: string]: any[]
    }
  },
  deviceState: {
    // remote controller, dock
    gatewayInfo: {} as {
      [sn: string]: GatewayOsd
    },
    // drone
    deviceInfo: {} as {
      [sn: string]: DeviceOsd
    },
    dockInfo: {} as {
      [sn: string]: DockOsd
    },
    currentSn: '',
    currentType: -1
  },
  osdVisible: { // osd 显示设备相关信息
    sn: '',
    callsign: '',
    model: '',
    visible: false,
    gateway_sn: '',
    is_dock: false,
    payloads: null
  } as OSDVisible,
  waylineInfo: {

  } as WaylineFile,
  dockInfo: {

  } as Device,
  hmsInfo: {} as {
    [sn: string]: DeviceHms[]
  },
  // 机场指令执行状态信息
  devicesCmdExecuteInfo: {
  } as DevicesCmdExecuteInfo,
  mqttState: null as any, // mqtt 实例
  clientId: '', // mqtt 连接 唯一客户端id
  skWaylineInfo: [], // 司空保存航线信息
  algorithmList: {},
  currentTask: '',
  waylineName: '',
  imageDiffProgress: null, // 图片对比进度
  waylineId: '', // 航线id
  // 智能助手对话列表（跨页面共用，避免路由切换清空）
  assistantMessages: [] as any[],
})

export type RootStateType = ReturnType<typeof initStateFunc> & {
  robot: RobotState
}

/** 根 state + robot 子模块，与 createStore 初始结构一致；用于退出登录等整表重置 */
export function getInitialStoreState (): RootStateType {
  return {
    ...initStateFunc(),
    robot: getRobotInitialState()
  }
}

const getters: GetterTree<RootStateType, RootStateType> = {
}
const mutations: MutationTree<RootStateType> = {
  SET_IMAGE_DIFF_PROGRESS (state, info) {
    state.imageDiffProgress = info
  },
  SET_SELECT_WAYLINE_ID (state, info) {
    state.waylineId = info
  },
  SET_ALGORITHM_LIST (state, info) {
    state.algorithmList = info
  },
  SET_SELECT_WAYLINE_NAME (state, info) {
    state.waylineName = info
  },
  SET_CURRENT_TASK (state, info) { // 当前任务号ID
    state.currentTask = info
  },
  SET_SIDEBAR_ROUTERS (state, info) {
    state.sidebarRouters = info
  },
  SET_DYNAMIC_ROUTES_REGISTERED (state, registered: boolean) {
    state.dynamicRoutesRegistered = registered
  },
  SET_SK_WAYLINE_INFO (state, info) {
    state.skWaylineInfo = info
  },
  SET_LAYER_INFO (state, info) {
    state.Layers = info
  },
  SET_DEVICE_POWER (state, power) {
    state.isPowered = power
  },
  SET_DEVICE_INFO (state, info) {
    state.deviceState.deviceInfo[info.sn] = info.host
    state.deviceState.currentSn = info.sn
    state.deviceState.currentType = EDeviceTypeName.Aircraft
    if (info.host.mode_code === 9) {
      sessionStorage.removeItem('isOpenAI')
      if (!sessionStorage.getItem('is_back')) {
        sessionStorage.setItem('is_back', '1')
        if (sessionStorage.getItem('algorithm_task_list')) {
          const task_list = JSON.parse(sessionStorage.getItem('algorithm_task_list') || '[]')
          task_list.forEach(el => {
            if (el.openTask) {
              setAlgorithmTaskStatus({
                taskNo: el.taskNo,
                taskStatus: 0
              }).then(res => {
                if (res.data.code === 0) {
                  // sessionStorage.removeItem("algorithm_task_list")
                }
              })
            }
          })
        }
      }
    }
    if (info.host.mode_code === 10 || info.host.mode_code === 11) {
      state.algorithmList = {}
      if (sessionStorage.getItem('is_fly') || sessionStorage.getItem('is_back')) {
        sessionStorage.removeItem('is_back')
        // sessionStorage.removeItem('algorithm_task_list')
        sessionStorage.removeItem('is_fly')
        localStorage.removeItem('taskNo')
        sessionStorage.removeItem('is_fly_video')
      }
      if (sessionStorage.getItem('rainfall')) {
        sessionStorage.removeItem('rainfall')
      }
    }
  },
  SET_GATEWAY_INFO (state, info) {
    state.deviceState.gatewayInfo[info.sn] = info.host
    state.deviceState.currentSn = info.sn
    state.deviceState.currentType = EDeviceTypeName.Gateway
  },
  SET_DOCK_INFO (state, info) {
    if (Object.keys(info.host).length === 0) {
      return
    }
    if (!state.deviceState.dockInfo[info.sn]) {
      state.deviceState.dockInfo[info.sn] = { } as DockOsd
    }
    state.deviceState.currentSn = info.sn
    state.deviceState.currentType = EDeviceTypeName.Dock
    const dock = state.deviceState.dockInfo[info.sn]
    if (info.host.mode_code !== undefined) {
      dock.basic_osd = info.host
      return
    }
    if (info.host.wireless_link) {
      dock.link_osd = info.host
      return
    }
    if (info.host.job_number !== undefined) {
      dock.work_osd = info.host
    }
  },
  SET_DRAW_VISIBLE_INFO (state, bool) {
    state.drawVisible = bool
  },
  SET_LIVESTREAM_OTHERS_VISIBLE (state, bool) {
    state.livestreamOthersVisible = bool
  },
  SET_LIVESTREAM_AGORA_VISIBLE (state, bool) {
    state.livestreamAgoraVisible = bool
  },
  SET_MAP_ELEMENT_CREATE (state, info) {
    state.wsEvent.mapElementCreat = info
  },
  SET_MAP_ELEMENT_UPDATE (state, info) {
    state.wsEvent.mapElementUpdate = info
  },
  SET_SPEAKER_AUDIO (state, info) {
    state.speakerAudio = info
  },
  SET_SPEAKER_VOLUME (state, info) {
    state.speakerVolume = info
  },
  SET_MAP_ELEMENT_DELETE (state, info) {
    state.wsEvent.mapElementDelete = info
  },
  SET_DEVICE_ONLINE (state, info) {
    state.deviceStatusEvent.deviceOnline = info
    // state.isPowered = true
  },
  SET_DEVICE_OFFLINE (state, info) {
    state.deviceStatusEvent.deviceOffline = info
    // state.isPowered = false
    delete state.deviceState.gatewayInfo[info.sn]
    delete state.deviceState.deviceInfo[info.sn]
    delete state.deviceState.dockInfo[info.sn]
    delete state.hmsInfo[info.sn]
    // delete state.markerInfo.coverMap[info.sn]
    // delete state.markerInfo.pathMap[info.sn]
  },
  SET_DEVICE_CLOSE (state, info) {
    delete state.deviceState.deviceInfo[info.sn]
  },
  SET_OSD_VISIBLE_INFO (state, info) {
    state.osdVisible = info
  },
  SET_SELECT_WAYLINE_INFO (state, info) {
    state.waylineInfo = info
  },
  SET_SELECT_DOCK_INFO (state, info) {
    state.dockInfo = info
  },
  SET_DEVICE_HMS_INFO (state, info) {
    const hmsList: Array<DeviceHms> = state.hmsInfo[info.sn]
    state.hmsInfo[info.sn] = info.host.concat(hmsList ?? [])
  },
  SET_DEVICES_CMD_EXECUTE_INFO (state, info) { // 保存设备指令ws消息推送
    if (!info.sn) {
      return
    }
    if (state.devicesCmdExecuteInfo[info.sn]) {
      const index = state.devicesCmdExecuteInfo[info.sn].findIndex(cmdExecuteInfo => cmdExecuteInfo.biz_code === info.biz_code)
      if (index >= 0) {
        // 丢弃前面的消息
        if (state.devicesCmdExecuteInfo[info.sn][index].timestamp > info.timestamp) {
          return
        }
        state.devicesCmdExecuteInfo[info.sn][index] = info
      } else {
        state.devicesCmdExecuteInfo[info.sn].push(info)
      }
    } else {
      state.devicesCmdExecuteInfo[info.sn] = [info]
    }
  },
  SET_MQTT_STATE (state, mqttState) {
    state.mqttState = mqttState
  },
  SET_CLIENT_ID (state, clientId) {
    state.clientId = clientId
  },
  // 设置/切换智能助手侧边栏显示状态
  SET_ASSISTANT_SIDEBAR_VISIBLE (state, visible: boolean) {
    state.showAssistantSidebar = visible
  },
  // 清空智能助手对话列表
  CLEAR_ASSISTANT_MESSAGES (state) {
    state.assistantMessages = []
  },
  // 追加一条助手消息
  PUSH_ASSISTANT_MESSAGE (state, msg: any) {
    state.assistantMessages.push(msg)
  },
  // 按 id 更新一条助手消息（用于进度、任务阶段等）
  UPDATE_ASSISTANT_MESSAGE (state, payload: { id: number, updates: any }) {
    const m = state.assistantMessages.find((m: any) => m.id === payload.id)
    if (m) Object.assign(m, payload.updates)
  },
}

const actions: ActionTree<RootStateType, RootStateType> = {
  async getAllElement ({ commit }) {
    const result = await getLayers({
      groupId: '',
      isDistributed: true
    })
    commit('SET_LAYER_INFO', result.data?.list)
  },
  updateElement ({ state }, content: {type: 'is_check' | 'is_select', id: string, bool:boolean}) {
    const key = content.id.replaceAll('resource__', '')
    const type = content.type
    const layers = state.Layers
    const layer = layers.find(item => item.id === key)
    if (layer) {
      layer[type] = content.bool
    }
  },
  setLayerInfo ({ state }, layers) {
    // const layers = state.Layers
    const obj:{
      [key:string]:string
    } = {}
    layers.forEach(layer => {
      if (layer.type === LayerType.Default) {
        obj.default = layer.id
      } else {
        if (layer.type === LayerType.Share) {
          obj.share = layer.id
        }
      }
    })
    state.layerBaseInfo = obj
  },
  getLayerInfo ({ state }, id:string) {
    return state.layerBaseInfo[id]
  },
  GenerateRoutes ({
    commit
  }) {
    return new Promise(resolve => {
      let menu: any[] = []
      try {
        const raw = localStorage.getItem(ELocalStorageKey.MenuList) || '[]'
        const parsed = JSON.parse(raw)
        menu = Array.isArray(parsed) ? parsed : []
      } catch {
        menu = []
      }
      // sessionStorage.setItem(ESessionStorageKey.MenuList, JSON.stringify(menu))
      // const menuList = [
      //   {
      //     path: '/' + ERouterName.NEW_HOME,
      //     name: ERouterName.NEW_HOME,
      //     component: 'Layout',
      //     children: [
      //       {
      //         path: '/' + ERouterName.MANAGE_DEVICE,
      //         meta: { title: '项目管理', no_cache: true },
      //         name: ERouterName.MANAGE_DEVICE,
      //         component: () => import('/@/pages/page-web/system/device/index.vue')
      //       },
      //       {
      //         path: '/' + ERouterName.MANAGE_MENU,
      //         meta: { title: '菜单管理', no_cache: true },
      //         name: ERouterName.MANAGE_MENU,
      //         component: () => import('/@/pages/page-web/system/menu/index.vue')
      //       },
      //       {
      //         path: '/' + ERouterName.MANAGE_REGION,
      //         meta: { title: '区域管理', no_cache: true },
      //         name: ERouterName.MANAGE_REGION,
      //         component: () => import('/@/pages/page-web/system/region/index.vue')
      //       },
      //       {
      //         path: '/' + ERouterName.MANAGE_ROLE,
      //         meta: { title: '角色管理', no_cache: true },
      //         name: ERouterName.MANAGE_ROLE,
      //         component: () => import('/@/pages/page-web/system/role/index.vue')
      //       },
      //     ]
      //   },
      // ]
      const imageAnnotatorRoute = {
        always_show: false,
        component: 'projects/imageAnnotator/index',
        hidden: true,
        icon: '',
        meta: { title: '图片标注', no_cache: true },
        name: ERouterName.IMAGE_ANNOTATOR,
        path: '/' + ERouterName.IMAGE_ANNOTATOR,
        perms_type: 15,
        id: 'image-annotator',
        order_num: 99,
        redirect: 'noRedirect'
      }
      const dogMediaFilesRoute = {
        always_show: false,
        component: 'robot-dog/media-files',
        hidden: true,
        icon: '',
        meta: { title: '机器狗媒体文件详情', no_cache: true },
        name: ERouterName.DOG_MEDIA_FILES,
        path: '/' + ERouterName.DOG_MEDIA_FILES,
        perms_type: 15,
        id: 'dog-media-files',
        order_num: 98,
        redirect: 'noRedirect'
      }
      const dogImageAnnotatorRoute = {
        always_show: false,
        component: 'robot-dog/imageAnnotator/index',
        hidden: true,
        icon: '',
        meta: { title: '机器狗图片标注', no_cache: true },
        name: ERouterName.DOG_IMAGE_ANNOTATOR,
        path: '/' + ERouterName.DOG_IMAGE_ANNOTATOR,
        perms_type: 15,
        id: 'dog-image-annotator',
        order_num: 99,
        redirect: 'noRedirect'
      }
      const workspaceExtraRoutes = [
            {
              always_show: true,
              component: 'projects/waylinePlan',
              hidden: false,
              icon: '',
              meta: { title: '航点航线', icon: '', no_cache: true, link: '/waylinePlanB' },
              name: 'waylinePlanB',
              path: '/waylinePlanB',
              perms_type: 15,
              parent_id: '1865930343211626497',
              id: '111',
              order_num: 13,
              redirect: 'noRedirect'
            },
            {
              always_show: true,
              component: 'projects/obliqueWayline/index',
              hidden: false,
              icon: '',
              meta: { title: '倾斜航线', icon: '', no_cache: true, link: '/oblique' },
              name: 'oblique',
              path: '/oblique',
              perms_type: 15,
              parent_id: '1865930343211626498',
              id: '112',
              order_num: 13,
              redirect: 'noRedirect'
            },
            {
              always_show: true,
              component: 'projects/planarWayline/index',
              hidden: false,
              icon: '',
              meta: { title: '正射航线', icon: '', no_cache: true, link: '/planar' },
              name: 'planar',
              path: '/planar',
              perms_type: 15,
              parent_id: '1865930343211626499',
              id: '112',
              order_num: 14,
              redirect: 'noRedirect'
            },
            {
              always_show: true,
              component: 'projects/inspection-collaboration',
              hidden: false,
              icon: '',
              meta: { title: '陆空一体协同巡检', icon: '', no_cache: true, link: '/inspection-collaboration' },
              name: 'InspectionCollaboration',
              path: '/inspection-collaboration',
              perms_type: 15,
              parent_id: '1865930343211626500',
              id: 'inspection-collaboration',
              order_num: 15,
              redirect: 'noRedirect'
            },
            {
              always_show: true,
              component: 'projects/inspection-collaboration-detail',
              hidden: true,
              icon: '',
              meta: { title: '协同巡检详情', icon: '', no_cache: true },
              name: 'InspectionCollaborationDetail',
              path: '/inspection-collaboration-detail',
              perms_type: 15,
              parent_id: '1865930343211626500',
              id: 'inspection-collaboration-detail',
              order_num: 16,
              redirect: 'noRedirect'
            },
            {
              always_show: true,
              component: 'projects/media-files',
              hidden: true,
              icon: '',
              meta: { title: '媒体文件详情', icon: '', no_cache: true },
              name: ERouterName.MEDIA_FILES,
              path: '/' + ERouterName.MEDIA_FILES,
              perms_type: 15,
              parent_id: '1865930343211626501',
              id: 'media-files',
              order_num: 18,
              redirect: 'noRedirect'
            },
            imageAnnotatorRoute,
      ]
      menu.forEach(route => {
        if (route.path === '/workspace') {
          route.children?.push(...workspaceExtraRoutes)
        }
        if (route.path === '/robot_dog') {
          route.children?.push(dogMediaFilesRoute, dogImageAnnotatorRoute)
        }
      })
      const sidebarRoutes = filterAsyncRoutes(menu)

      commit('SET_SIDEBAR_ROUTERS', sidebarRoutes)
      commit('SET_DYNAMIC_ROUTES_REGISTERED', true)

      resolve(sidebarRoutes)
    })
  }
}
/**
 * 递归过滤有权限的动态路由
 *
 * @param routes 接口返回所有的动态路由
 * @returns 返回用户有权限的动态路由
 */
const filterAsyncRoutes = (routes: any) => {
  // const Layout = () => import('/@/pages/page-web/newHome.vue')
  const asyncRoutes: RouteRecordRaw[] = []
  routes.forEach((route:any) => {
    const tmpRoute = { ...route } as RouteRecordRaw // 深拷贝 route 对象 避免污染
    const routePath = typeof tmpRoute.path === 'string' ? tmpRoute.path : ''
    applyRobotDogPageMeta(tmpRoute)
    applyWorkspacePageMeta(tmpRoute)
    tmpRoute.redirect = null
    if (tmpRoute.perms_type === 15) {
      if (
        tmpRoute.path === '/task' ||
        tmpRoute.path === '/create-plan' ||
        tmpRoute.path === '/robot_device' ||
        tmpRoute.path === '/robot_home'
      ) {
      } else {
        delete tmpRoute.children
      }
    }
    // 如果是顶级目录，替换为 Layout 组件
    if (tmpRoute.component?.toString() === 'Layout') {
      if (tmpRoute.path === '/home') {
        tmpRoute.component = () => import('/@/pages/page-web/home.vue')
      } else if (tmpRoute.path === '/new_home') {
        tmpRoute.component = () => import('/@/pages/page-web/newHome.vue')
      } else if (tmpRoute.path === '/robot_dog') {
        tmpRoute.component = () => import('/@/pages/page-web/robotDogHome.vue')
      } else if (tmpRoute.path === '/robot_home' || tmpRoute.path === '/robot_device') {
        // /robot_device 若为 Layout 根，同样挂机器人管控壳（含智能仓储侧栏切换）
        tmpRoute.component = () => import('/@/pages/page-web/robotHome.vue')
      } else if (tmpRoute.path === '/algorithm') {
        tmpRoute.component = algorithmPlan
        tmpRoute.redirect = '/model'
      } else {
        tmpRoute.component = () => import('/@/pages/page-web/projects/workspace.vue')
      }
    } else {
      // 如果是子目录，动态加载组件
      if (tmpRoute.component) {
        if (tmpRoute.path === '/create-plan') {
          tmpRoute.component = CreatePlan
        } else if (tmpRoute.path === '/wayline-panel') {
          tmpRoute.component = WaylinePanel
        } else if (tmpRoute.path === '/dock-panel') {
          tmpRoute.component = DockPanel
        } else if (tmpRoute.path === '/center') {
          tmpRoute.component = AlarmCenter
        } else if (tmpRoute.path === '/model') {
          tmpRoute.component = Model
        } else if (tmpRoute.path === '/binding') {
          tmpRoute.component = Binding
        } else if (tmpRoute.path === '/pushLog') {
          tmpRoute.component = PushLog
        } else if (tmpRoute.path === '/inspection-collaboration') {
          tmpRoute.component = () => import('/@/pages/page-web/projects/inspection-collaboration.vue')
        } else if (tmpRoute.path === '/inspection-collaboration-detail') {
          tmpRoute.component = () => import('/@/pages/page-web/projects/inspection-collaboration-detail.vue')
          tmpRoute.meta = {
            ...(tmpRoute.meta || {}),
            pageComponent: 'projects/inspection-collaboration-detail',
            isDetailPage: true,
          }
        } else if (tmpRoute.path === '/media-files') {
          tmpRoute.component = () => import('/@/pages/page-web/projects/media-files.vue')
          tmpRoute.meta = {
            ...(tmpRoute.meta || {}),
            pageComponent: 'projects/media-files',
            cacheName: 'media_files',
            isDetailPage: true,
          }
        } else if (tmpRoute.path === '/dog_media_center') {
          tmpRoute.component = () => import('/@/pages/page-web/robot-dog/mediaCenter.vue')
          tmpRoute.meta = {
            ...(tmpRoute.meta || {}),
            pageComponent: ROBOT_DOG_MEDIA_COMPONENT,
            cacheName: ROBOT_DOG_MEDIA_CACHE_NAME,
          }
        } else if (tmpRoute.path === '/' + ERouterName.DOG_MEDIA_FILES) {
          tmpRoute.component = () => import('/@/pages/page-web/robot-dog/media-files.vue')
          tmpRoute.meta = {
            ...(tmpRoute.meta || {}),
            pageComponent: 'robot-dog/media-files',
            cacheName: ROBOT_DOG_MEDIA_FILES_CACHE_NAME,
            isDetailPage: true,
          }
        } else if (tmpRoute.path === '/' + ERouterName.IMAGE_ANNOTATOR) {
          tmpRoute.component = () => import('/@/pages/page-web/projects/imageAnnotator/index.vue')
          tmpRoute.meta = {
            ...(tmpRoute.meta || {}),
            pageComponent: 'projects/imageAnnotator/index',
          }
        } else if (tmpRoute.path === '/' + ERouterName.DOG_IMAGE_ANNOTATOR) {
          tmpRoute.component = () => import('/@/pages/page-web/robot-dog/imageAnnotator/index.vue')
          tmpRoute.meta = {
            ...(tmpRoute.meta || {}),
            pageComponent: 'robot-dog/imageAnnotator/index',
          }
        } else if (
          tmpRoute.name === ERouterName.MAP_DETAIL ||
          tmpRoute.path === '/map_detail'
        ) {
          tmpRoute.component = () => import('/@/pages/page-web/robot-dog/mapDetail.vue')
          tmpRoute.meta = {
            ...(tmpRoute.meta || {}),
            pageComponent: 'robot-dog/mapDetail',
            isDetailPage: true,
          }
        } else {
          tmpRoute.component = modules[`../pages/page-web/${tmpRoute.component}.vue`]
        }
      }
    }

    if (tmpRoute.children) {
      tmpRoute.children = filterAsyncRoutes(route.children)
    }

    asyncRoutes.push(tmpRoute)
  })

  return asyncRoutes
}
const storeOptions: StoreOptions<RootStateType> = {
  state: initStateFunc,
  modules: {
    robot: robotModule
  },
  getters,
  mutations,
  actions
}

const rootStore = createStore(storeOptions)

export default rootStore

// 使用 Symbol.for 保证在 HMR/重复加载时注入的 key 仍然是同一个全局 symbol
export const storeKey: InjectionKey<Store<RootStateType>> = Symbol.for('airplan-control-frontend.vuex-storeKey') as any

type AllStateStoreTypes = RootStateType & {
  // 其他 Vuex 子模块类型可在此交叉合并
}

export function useMyStore<T = AllStateStoreTypes> () {
  return useStore<T>(storeKey)
}
